import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./ColorMatch.css";

const COLOR_SETS = {
  easy: ["rojo", "azul", "verde", "amarillo"],
  medium: ["rojo", "azul", "verde", "amarillo", "morado"],
  hard: ["rojo", "azul", "verde", "amarillo", "morado", "naranja"],
};

const COLOR_EMOJI = {
  rojo: "🔴",
  azul: "🔵",
  verde: "🟢",
  amarillo: "🟡",
  morado: "🟣",
  naranja: "🟠",
};

const GAME_CONFIG = {
  easy: {
    totalRounds: 8,
    patternLength: 3,
    colors: COLOR_SETS.easy,
    label: "Fácil",
  },
  medium: {
    totalRounds: 10,
    patternLength: 4,
    colors: COLOR_SETS.medium,
    label: "Media",
  },
  hard: {
    totalRounds: 15,
    patternLength: 5,
    colors: COLOR_SETS.hard,
    label: "Difícil",
  },
};

function randomColor(colors) {
  return colors[Math.floor(Math.random() * colors.length)];
}

function createPattern(length, colors) {
  return Array.from({ length }, () => randomColor(colors));
}

function createRound(config) {
  const target = createPattern(config.patternLength, config.colors);
  const shouldMatch = Math.random() > 0.5;

  if (shouldMatch) {
    return {
      target,
      comparison: [...target],
      correctAnswer: "same",
    };
  }

  const comparison = [...target];
  const indexToChange = Math.floor(Math.random() * comparison.length);

  let newColor = randomColor(config.colors);

  while (newColor === comparison[indexToChange]) {
    newColor = randomColor(config.colors);
  }

  comparison[indexToChange] = newColor;

  return {
    target,
    comparison,
    correctAnswer: "different",
  };
}

export default function ColorMatch() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setStarted(true);
    setFinished(false);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound(nextConfig));
  };

  const answer = (value) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = value === round.correctAnswer;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: value,
        correctAnswer: round.correctAnswer,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) {
      setCorrect((prev) => prev + 1);
    } else {
      setErrors((prev) => prev + 1);
    }

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Color Match"
        description="Compara patrones de colores y decide si son iguales o diferentes."
        areas={["Atención", "Memoria visual", "Control inhibitorio"]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="color-match-page">
      <div className="color-match-card">
        <header className="color-match-header">
          <div>
            <h1>Color Match</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            {started ? "Reiniciar" : finished ? "Jugar otra vez" : "Empezar"}
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="patterns">
              <div className="pattern-block">
                <span>Modelo</span>
                <div className="pattern-row">
                  {round.target.map((color, index) => (
                    <div key={`target-${color}-${index}`} className="color-token">
                      {COLOR_EMOJI[color]}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pattern-block">
                <span>Comparar</span>
                <div className="pattern-row">
                  {round.comparison.map((color, index) => (
                    <div
                      key={`comparison-${color}-${index}`}
                      className="color-token"
                    >
                      {COLOR_EMOJI[color]}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="answer-buttons">
              <button type="button" onClick={() => answer("same")}>
                Iguales
              </button>

              <button type="button" onClick={() => answer("different")}>
                Diferentes
              </button>
            </section>

            <section className="live-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}